import { useAppState } from 'hooks/useAppState';
import { useEffect, useState, type JSX } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import { SignupCompanySection } from './components/SignupCompanySection';
import { SignupFooterActions } from './components/SignupFooterActions';
import { SignupUserSection } from './components/SignupUserSection';
import { useSignupFormRestore } from './hooks/useSignupFormRestore';
import { submit } from 'utils/app';

/**
 * アカウント登録画面
 */

export type SignupFormStateType = {
  mailAddress: string;
  fullName: string;
  companyName: string;
  tel: string;
  zipCode: string;
  prefectureDiv: string;
  city: string;
  address: string;
  buildingName: string;
};

export default function SignupIndex(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();

  const { signUpRequest } = useAppState();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [acctState] = useAtom(AcctState);

  const {
    register,
    handleSubmit,
    setError: setFormError,
    setValue,
    formState: { errors },
  } = useForm<SignupFormStateType>();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useSignupFormRestore(setValue, setFormError);

  useEffect(() => {
    // ログイン済の場合は、カンパニー選択画面に遷移
    if (acctState.user) {
      navigate('/signin/company');
    }
  }, [acctState.user, navigate]);

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 登録ボタン押下時の処理
   */
  const onSubmit = () =>
    submit(
      processing,
      setProcessing,
      handleSubmit(async (data: SignupFormStateType) => {
        try {
          // サインアップリクエストを送信
          await signUpRequest(data.mailAddress);
          setError('');

          // ログイン認証コード入力画面に遷移
          navigate('/signin/auth', {
            state: {
              mailAddress: data.mailAddress,
              nextTo: '/',
              signupData: data,
            },
          });
        } catch (e: unknown) {
          if (e instanceof Error && e.name === 'UsernameExistsException') {
            setError('すでに登録されているメールアドレスです。');
          } else {
            showBoundary(e);
          }
        }
      })
    );

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <section className="section-header">
        <div className="container">
          <div className="d-flex justify-content-center">
            <div className="text-center" style={{ marginTop: '3rem' }}>
              <img
                src="/img/common/logo.svg"
                alt="BranQ ブランク"
                className="d-block mx-auto mb-4"
                style={{ maxWidth: '200px' }}
              />
              <h2 className="flex-title">
                30日間の無料トライアル（スタンダートプラン）をはじめる。
              </h2>
            </div>
          </div>
        </div>
      </section>
      <section className="section-basic-setting">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-sm-8 col-12">
              <div className="box-common pb-1">
                <form className="form-profile g-3">
                  <div className="form-group m-0 mb-5">
                    <SignupUserSection register={register} errors={errors} />
                    <SignupCompanySection register={register} errors={errors} />
                    <SignupFooterActions
                      processing={processing}
                      errorMessage={error}
                      onSubmit={onSubmit}
                      onGoSignIn={() => navigate('/signin')}
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* コピーライト */}
      <div className="mt-2 text-center mb-2">
        <small className="text-muted">©BranQ Inc.</small>
      </div>
    </>
  );
}
